// Base API URL (auto-switch by environment)
const host = window.location.hostname;

const isLocal =
    host === 'localhost' ||
    host === '127.0.0.1' ||
    host.endsWith('.localhost');

const isSubdomainOf = (domain: string) =>
    host.endsWith(`.${domain}`) && host !== domain;

const getSubdomain = (domain: string) => {
    if (!isSubdomainOf(domain)) return null;
    return host.slice(0, -(domain.length + 1));
};

const familySafeSubdomain = getSubdomain('familysafe.co.uk');
const companySafeSubdomain = getSubdomain('companysafe365.co.uk');
const businessSafeSubdomain = getSubdomain('businesssafe360.co.uk');

const isReservedSubdomain = (subdomain: string | null) =>
    subdomain === 'www' || subdomain === 'api';

const isCustomFamilySafe =
    familySafeSubdomain !== null && !isReservedSubdomain(familySafeSubdomain);

const isCustomCompanySafe =
    companySafeSubdomain !== null && !isReservedSubdomain(companySafeSubdomain);

const isCustomBusinessSafe =
    businessSafeSubdomain !== null && !isReservedSubdomain(businessSafeSubdomain);

const isCustomTenantHost =
    isCustomFamilySafe || isCustomCompanySafe || isCustomBusinessSafe;

const isPreview =
    host === 'preview.familysafe.uk' ||
    host === 'preview.businesssafe360.co.uk';
export const CARD_TEMPLATE_PUBLICATION_SOURCE_ENABLED = isLocal || isPreview;
const isVaultProductionHost =
    host === 'vault.familysafe.co.uk';

const getApiRoute = () => {
    if (isLocal) {
        return 'http://localhost:5239/api/v1/';
    }

    switch (host) {
        case 'webpreview.familysafe.uk':
        case 'preview.familysafe.uk':
        case 'preview.businesssafe360.co.uk':
            return 'https://apipreview.familysafe.uk/api/v1/';
        case 'vault.familysafe.co.uk':
            return 'https://api.familysafe.co.uk/api/v1/';

        default:
            return isCustomTenantHost || isVaultProductionHost
                ? 'https://api.familysafe.co.uk/api/v1/'
                : '';
    }
};
export const API_ROUTE = getApiRoute();


export const API_ROUTEe =
    isLocal ? 'http://localhost:5239/api/v1/' :
        isPreview ? 'https://apipreview.familysafe.uk/api/v1/' :
            'https://api.familysafe.co.uk/api/v1/';


// Signup consent links use the published legal documents in every environment.
export const TERMS_URL = 'https://www.familysafe.co.uk/legal/terms-conditions';
export const PRIVACY_POLICY_URL = 'https://www.familysafe.co.uk/legal/privacy-policy';

export const WEBSITE =
    isLocal ? 'http://localhost:3001/' :
        isPreview ? 'https://webpreview.familysafe.uk/' :
            isCustomTenantHost ? `https://${host}/` :
                'https://www.familysafe.co.uk/';



/**
 * API Route Definitions
 * Routes are mapped from old naming to new RESTful endpoints
 */
export const Routes = {
    // AUTHENTICATION & AUTHORIZATION (user/* -> auth/*)
    SIGNUP: 'auth/signup',                    // OLD: user/create
    AUTH_RESEND_INVITE: 'auth/resend-invite',
    LOGIN: 'auth/login',                      // OLD: user/login
    LOGIN_SALT: 'auth/login-salt',
    REFERRALS_VALIDATE: 'referrals/validate',
    REFRESH_TOKEN: 'auth/refresh',            // OLD: user/refresh-token
    LOGOUT: 'auth/logout',                    // OLD: user/logout
    ACTIVATE: 'auth/activate',                // Activate user account
    ACTIVATE_INVITE: 'auth/activate-invite',  // Activation for invited users
    AUTH_METHODS: 'auth/methods',
    AUTH_EMAIL_OTP_SEND: 'auth/email-otp/send',
    AUTH_EMAIL_OTP_VERIFY: 'auth/email-otp/verify',
    AUTH_TOTP_SETUP: 'auth/totp/setup',
    AUTH_TOTP_VERIFY: 'auth/totp/verify',
    AUTH_PASSKEY_REGISTER_OPTIONS: 'auth/passkeys/register/options',
    AUTH_PASSKEY_REGISTER_VERIFY: 'auth/passkeys/register/verify',
    AUTH_PASSKEY_LOGIN_OPTIONS: 'auth/passkeys/login/options',
    AUTH_PASSKEY_LOGIN_VERIFY: 'auth/passkeys/login/verify',
    AUTH_RECOVERY_CODES_REGENERATE: 'auth/recovery-codes/regenerate',
    AUTH_RECOVERY_CODES_VERIFY: 'auth/recovery-codes/verify',
    AUTH_METHODS_PRIMARY: 'auth/methods/primary',
    AUTH_METHODS_REMOVE: 'auth/methods/{methodCode}',
    AUTH_PASSKEY_REMOVE: 'auth/passkeys/{credentialId}',
    GET_USER_MENU: 'users/me/menus',          // OLD: user/menu
    GET_USER_FOOTER_MENU: 'users/me/footer-menu',
    GET_PUBLIC_FOOTER_MENU: 'users/footer-menu',

    // PARTNER PORTAL (referral programme)
    PARTNER_ACCESS: 'partner/access',
    PARTNER_DASHBOARD: 'partner/dashboard',
    PARTNER_USERS: 'partner/users',
    PARTNER_REFERRALS: 'partner/referrals',
    PARTNER_COMMISSIONS: 'partner/commissions',
    PARTNER_COMMISSION_STATEMENT_EMAIL: 'partner/commission-statements/email',
    PARTNER_INTRODUCED_ORGANISATIONS: 'partner/introduced-organisations',
    PARTNER_MARKETPLACE_PROFILE: 'partner/marketplace/profile',
    PARTNER_MARKETPLACE_LEADS: 'partner/marketplace/leads',
    PARTNER_MARKETPLACE_LEAD_STATUS: 'partner/marketplace/leads/{leadId}/status',

    // MARKETPLACE
    MARKETPLACE_CATEGORIES: 'marketplace/categories',
    MARKETPLACE_PARTNERS: 'marketplace/partners',
    MARKETPLACE_PARTNER: 'marketplace/partners/{profileId}',
    MARKETPLACE_PARTNER_LEADS: 'marketplace/partners/{profileId}/leads',
    MARKETPLACE_PARTNER_REFERRAL_CLICK: 'marketplace/partners/{profileId}/referral-click',

    // CATEGORIES (Categories/* -> categories/*)
    GET_CATEGORIES: 'categories',                                    // OLD: Categories/GetCategory
    GET_DASHBOARD_CATEGORIES: 'categories/dashboard-list',
    GET_ALL_SUBCATEGORIES: 'categories/{categoryId}/subcategories',  // OLD: Categories/Subcategories
    GET_USER_CATEGORIES_SUMMARY: 'categories/summary',
    
    // CARD TEMPLATES (GetCardTemplate* -> cardtemplates/*)
    GET_CARDTEMPLATE: 'cardtemplates',                                // OLD: GetCardTemplate
    GET_CARDTEMPLATE_By_Category: 'cardtemplates',                    // OLD: GetCardTemplatesByCategory (use ?categoryId=)
    GET_CARDTEMPLATE_WITH_FIELDS: 'cardtemplates/{cardTemplateId}/fields',  // OLD: GetCardTemplateWithFields
    GET_FIELDS_BY_CARDID: 'cardtemplates/{cardTemplateId}/datacards', // OLD: GetFieldsByCardTemplate
    CARD_TEMPLATE_PRODUCTION_SYNC: 'admin/card-templates/{templateId}/production-sync',
    CARD_TEMPLATE_PRODUCTION_SYNC_BATCH: 'admin/card-templates/production-sync',
    CARD_TEMPLATE_PRODUCTION_DIFF: 'admin/card-templates/{templateId}/production-diff',
    CARD_TEMPLATE_VALIDATE_PUBLICATION: 'admin/card-templates/{templateId}/validate-publication',
    CARD_TEMPLATE_PUBLISH: 'admin/card-templates/{templateId}/publish',
    CARD_TEMPLATE_PULL_FROM_PRODUCTION: 'admin/card-templates/{templateId}/pull-from-production',
    CARD_TEMPLATE_DEPLOYMENT_HISTORY: 'admin/card-templates/{templateId}/deployment-history',
    CREATE_DATACARD: 'cardtemplates',                                 // OLD: CreateCardDefinition
    UPDATE_DATACARD: 'cardtemplates/{cardTemplateId}',               // OLD: UpdateCardDefinition

    // FIELD TEMPLATES (CreateField/UpdateField -> fieldtemplates/*)
    GET_FIELDTEMPLATE: 'fieldtemplates',                  // OLD: GetFieldTemplate
    CREATE_FIELD: 'fieldtemplates',                       // OLD: CreateField
    UPDATE_FIELD: 'fieldtemplates/{fieldTemplateId}',    // OLD: UpdateField
    
    // FIELD GROUPS (FieldGroups -> fieldgroups)
    GET_ALL_FIELDGROUPS: 'fieldgroups',                   // OLD: FieldGroups

    // DATA CARDS (Get*/Set*/Update* -> datacards/*)
    GET_ALL_DATACARD: 'datacards',                        // OLD: GetAllDataCard
    SEARCH_CARD_METADATA: 'datacards/search',
    GET_DATACARD_BY_TEMPLATES: 'datacards',               // OLD: GetDataCardsByTemplates (use ?categoryId=)
    GET_DATACARD: 'datacards/{cardId}',                   // OLD: GetDataCardDetails
    SET_FIELDS_VALUE: 'datacards',                        // OLD: SetCardFieldValue (POST)
    UPDATE_FIELDS_VALUE: 'datacards/{cardId}',            // OLD: UpdateCardFieldValue (PUT)
    CREATE_FILE_UPLOAD_URL: 'datacards/{cardId}/files/upload-url',
    DELETE_DATACARD: 'cards/{cardId}/delete',
    GET_CHILDCARD_DATA: 'datacards/{cardId}/children',    // OLD: GetChildCardsData
    GET_PARENTCARD: 'datacards/{cardId}/parent',          // OLD: GetOwnerCard
    LINK_TO_PARENT_CARD: 'datacards/{cardId}/parent',     // OLD: LinkToParentCard (PUT)
    GET_USER_CARDS_SUMMARY: 'datacards/{categoryId}/summary',
    GET_USER_CARD_LIST_SUMMARY: 'datacards/{categoryId}/list-summary',
    GET_USER_CARD_VIEW_SUMMARY: 'datacards/{categoryId}/view-summary',
    GET_USER_CARD_VIEW_LIST: 'datacards/{categoryId}/view-list',
    UPLOAD_CARD_PHOTO: 'datacards/{cardId}/photo',
    UPDATE_CARD_PHOTO: 'datacards/{cardId}/photo',
    GET_CARD_PHOTO: 'datacards/{cardId}/photo',
    DELETE_CARD_PHOTO: 'datacards/{cardId}/photo',

    // DOCUMENT PROCESSING
    DOCUMENT_PROCESSING_TEMPLATES_WITH_OCR_PROFILE: 'document-processing/templates-with-ocr-profile',
    DOCUMENT_PROCESSING_CREATE_JOB: 'document-processing/jobs',
    DOCUMENT_PROCESSING_JOB: 'document-processing/jobs/{id}',
    DOCUMENT_PROCESSING_DRAFT: 'document-processing/jobs/{id}/draft',
    DOCUMENT_PROCESSING_TEMPLATE_SUGGESTIONS: 'document-processing/jobs/{id}/template-suggestions',
    DOCUMENT_PROCESSING_SOURCE_FILE: 'document-processing/jobs/{id}/source-file',
    DOCUMENT_PROCESSING_CONFIRM: 'document-processing/jobs/{id}/confirm',
    DOCUMENT_PROCESSING_CANCEL: 'document-processing/jobs/{id}/cancel',
    DOCUMENT_PROCESSING_CLEAR_CACHE: 'document-processing/jobs/{id}/clear-cache',
    ADMIN_DOCUMENT_PROCESSING_SUMMARY: 'admin/document-processing/summary',
    ADMIN_DOCUMENT_PROCESSING_QUEUE: 'admin/document-processing/queue',
    ADMIN_DOCUMENT_PROCESSING_QUEUE_ITEM: 'admin/document-processing/queue/{jobId}',
    ADMIN_DOCUMENT_PROCESSING_QUEUE_LOGS: 'admin/document-processing/queue/{jobId}/logs',
    ADMIN_DOCUMENT_PROCESSING_AUDIT: 'admin/document-processing/audit',
    ADMIN_DOCUMENT_PROCESSING_LEARNING_EVENTS: 'admin/document-processing/learning/events',
    ADMIN_DOCUMENT_PROCESSING_LEARNING_EVENT: 'admin/document-processing/learning/events/{eventId}',
    ADMIN_DOCUMENT_PROCESSING_LEARNING_RULES: 'admin/document-processing/learning/rules',
    ADMIN_DOCUMENT_PROCESSING_LEARNING_RULE: 'admin/document-processing/learning/rules/{ruleId}',
    ADMIN_DOCUMENT_PROCESSING_OCR_PROFILES: 'admin/document-processing/ocr-profiles',
    ADMIN_DOCUMENT_PROCESSING_OCR_PROFILE: 'admin/document-processing/ocr-profiles/{profileId}',
    ADMIN_READINESS_RULES: 'admin/readiness/rules',
    ADMIN_READINESS_RULE: 'admin/readiness/rules/{id}',
    ADMIN_READINESS_RULES_EXPORT: 'admin/readiness/rules/export',
    ADMIN_READINESS_RULES_IMPORT: 'admin/readiness/rules/import',
    ADMIN_READINESS_STATUS_BANDS: 'admin/readiness/status-bands',
    ADMIN_READINESS_STATUS_BAND: 'admin/readiness/status-bands/{id}',
    ADMIN_READINESS_MILESTONES: 'admin/readiness/milestones',
    ADMIN_READINESS_MILESTONE: 'admin/readiness/milestones/{id}',
    ADMIN_READINESS_MILESTONES_EXPORT: 'admin/readiness/milestones/export',
    ADMIN_READINESS_MILESTONES_IMPORT: 'admin/readiness/milestones/import',
    ADMIN_READINESS_ACTIONS: 'admin/readiness/actions',
    ADMIN_READINESS_ACTION: 'admin/readiness/actions/{id}',
    ADMIN_READINESS_ACTIONS_EXPORT: 'admin/readiness/actions/export',
    ADMIN_READINESS_ACTIONS_IMPORT: 'admin/readiness/actions/import',
    ADMIN_READINESS_EVENTS: 'admin/readiness/events',
    ADMIN_READINESS_PROFILES: 'admin/readiness/profiles',
    ADMIN_READINESS_PROFILE_RECALCULATE: 'admin/readiness/profiles/{id}/recalculate',
    READINESS_ME: 'readiness/me',
    READINESS_ME_EVENTS: 'readiness/me/events',
    READINESS_ME_MILESTONES: 'readiness/me/milestones',
    READINESS_MILESTONE_SEEN: 'readiness/me/milestones/{milestoneId}/seen',
    READINESS_ME_ACTIONS: 'readiness/me/actions',
    READINESS_ACTION_COMPLETE: 'readiness/actions/{id}/complete',
    READINESS_ACTION_DISMISS: 'readiness/actions/{id}/dismiss',
    READINESS_ACTION_SNOOZE: 'readiness/actions/{id}/snooze',

    // REMINDERS
    GET_REMINDERS: 'reminders',
    GET_REMINDER_FOR_FIELD: 'reminders/card/{cardId}/field/{fieldId}',
    UPSERT_REMINDER: 'reminders/card/{cardId}/field/{fieldId}',
    DELETE_REMINDER: 'reminders/card/{cardId}/field/{fieldId}',

    // FILES (GetEncryptedFile -> files/*)
    GET_ENCRYPTED_FILE: 'files/{cardId}',                 // OLD: GetEncryptedFile (use ?ext=)

    // KEYS (user/* -> keys/*)
    GET_KEYCOUNT: 'keys/count',                           // OLD: user/getKeyCount
    ADD_MISSING_KEYS: 'keys',                             // OLD: user/default-keys
    GET_KEYS_GROUP: 'keys',                                       // OLD: share/kg (use ?categoryId=)

    // SHARING (share/* -> shares/*)
    SHARE_ITEM: 'shares',                                 // OLD: share/item
    SHARE_DETAIL: 'categories/{categoryId}/sharing',  

    SHARE: "shares",                                    // POST with ?type=card|category|global
    GET_SHARED_ITEMS: "shares/users/{idUser}",   // GET with ?type=card|category|global
    GET_MANAGED_SHARES: "shares/manage",
    GET_PENDING_SHARES: 'shares/pending',               // GET pending shares with encrypted keys
    COMPLETE_SHARES: 'shares/incomplete',               // POST to get incomplete shares data
    FINALIZE_SHARES: 'shares/finalize',                 // POST to finalize pending shares with re-encrypted keys
    REMOVE_PENDING_SHARE: 'shares/pending/users/{idUserShare}', // DELETE pending shares for a user
    UNSHARE_ITEM: 'shares/users/{idUserShare}',          // DELETE with ?keyUser={idKeyUser}&card={cardId}

    // PROBATE (Probate/* -> probates/*)
    GET_PROBATE_LIST: 'probates/items',                               // OLD: Probate/GetProbateList
    SAVE_PROBATE_NOTES: 'probates/logs/{cardTemplateId}',            // OLD: Probate/SaveProbateNotes
    GET_PROBATELOG: 'probates/logs/{cardTemplateId}',                // OLD: Probate/GetProbateLog

    // FAMILIES (user/* -> families/*)
    FAMILY_MEMBERS: 'families/members',               // OLD: user/family-members
    FAMILY_MEMBERS_ADMIN_VIEW: 'families/family-members/visible',
    ADVISORS: 'families/advisors',                    // OLD: user/getAllMembers
    // helper endpoints used by the frontend when inviting new users  
    USER_FAMILIES: 'families',                // GET personal families; POST creates one
    PENDING_INVITES: 'families/pending-invites',
    REMOVE_FAMILIES_STATUS: 'families/remove/member',
    UPDATE_FAMILIES_STATUS: 'families/member/status',
    
    // PACKAGES (user/* -> packages)
    USER_PACKAGES: 'users/me/packages',
    USER_EFFECTIVE_PACKAGE: 'users/me/effective-package',
    UPDATE_USER_PACKAGES: 'users/me/packages/{packageId}',
    GET_PACKAGES: 'packages',                             // OLD: user/allpackages
    ADMIN_GET_PACKAGES: 'packages/admin/all',

    // USERS (users-list/* -> users/*)
    GET_USER_SUMMARY: 'users',                            // OLD: users-list
    GET_ADMIN_USER_DETAIL: 'users/{userId}',
    UPDATE_ADMIN_USER: 'users/{userId}',
    DELETE_USER: 'users/{userId}',                        // OLD: users-list/delete-user
    DISABLE_USER_MFA: 'users/{userId}/auth/disable-mfa',
    ADMIN_USER_TAKE_ON_RESET: 'users/{userId}/take-on/reset',
    GET_USERSUMMARY: 'users/summary',
    UPDATE_USER_STATUS: 'users/update-status/{userId}',
    GET_USER_ACTIVITY_LOG: 'users/activity-log/{userId}',

    //INVOICE
    GET_INVOICES: 'invoice',
    GET_DOWNLOAD_INVOICE:'invoice/{invoiceNo}/download',
    BILLING_MANDATE_CHANGE: 'billing/mandate-change',
    BILLING_MANDATE_CHANGE_STATUS: 'billing/mandate-change/status',
    ADMIN_INVOICES_SUMMARY: 'admin/invoices/summary',
    ADMIN_INVOICES_CYCLE_EVENTS: 'admin/invoices/{invoiceNo}/cycle-events',
    ADMIN_INVOICES_RUN_BATCH: 'admin/invoices/run-batch',
    ADMIN_INVOICES_RUN_USER: 'admin/invoices/due-users/{userId}/run',
    ADMIN_WEBHOOKS_SUMMARY: 'admin/webhooks/summary',
    ADMIN_WEBHOOKS_RULE: 'admin/webhooks/rules/{ruleId}',
    ADMIN_PARTNERS_SUMMARY: 'admin/partners/summary',
    ADMIN_PARTNERS: 'admin/partners',
    ADMIN_PARTNER: 'admin/partners/{partnerId}',
    ADMIN_BRANDS: 'admin/brands',
    ADMIN_BRAND: 'admin/brands/{brandId}',
    ADMIN_PORTALS: 'admin/portals',
    ADMIN_PORTAL: 'admin/portals/{portalId}',
    ADMIN_PORTAL_DEFAULT: 'admin/portals/{portalId}/default',
    ADMIN_REFERRAL_PARTNERS_SUMMARY: 'admin/referral-partners/summary',
    ADMIN_REFERRAL_PARTNERS: 'admin/referral-partners',
    ADMIN_REFERRAL_PARTNER: 'admin/referral-partners/{partnerId}',
    ADMIN_REFERRAL_PARTNER_GENERATE_CODE: 'admin/referral-partners/{partnerId}/generate-referral-code',
    ADMIN_REFERRAL_PARTNER_LINK_USER: 'admin/referral-partners/{partnerId}/link-user',
    ADMIN_REFERRAL_PARTNER_LINK_INTRODUCER: 'admin/referral-partners/{partnerId}/link-introducer',
    ADMIN_REFERRAL_PARTNER_REFERRALS: 'admin/referral-partners/{partnerId}/referrals',
    ADMIN_REFERRAL_PARTNER_CUSTOMERS: 'admin/referral-partners/{partnerId}/customers',
    ADMIN_REFERRAL_PARTNER_CUSTOMER: 'admin/referral-partners/{partnerId}/customers/{userId}',
    ADMIN_REFERRAL_PARTNER_ALL_COMMISSIONS: 'admin/referral-partners/commissions',
    ADMIN_REFERRAL_PARTNER_COMMISSIONS: 'admin/referral-partners/{partnerId}/commissions',
    ADMIN_REFERRAL_PARTNER_INTRODUCED_ORGANISATIONS: 'admin/referral-partners/{partnerId}/introduced-organisations',
    ADMIN_REFERRAL_PARTNER_INTRODUCED_ORGANISATION: 'admin/referral-partners/{partnerId}/introduced-organisations/{organisationPartnerId}',
    ADMIN_REFERRAL_PARTNERS_ELIGIBILITY_CHECK: 'admin/referral-partners/eligibility-check',
    ADMIN_MARKETPLACE_PARTNERS: 'admin/marketplace/partners',
    ADMIN_MARKETPLACE_PARTNER: 'admin/marketplace/partners/{profileId}',
    ADMIN_MARKETPLACE_PARTNER_APPROVE: 'admin/marketplace/partners/{profileId}/approve',
    ADMIN_MARKETPLACE_PARTNER_REJECT: 'admin/marketplace/partners/{profileId}/reject',
    ADMIN_MARKETPLACE_PARTNER_SUSPEND: 'admin/marketplace/partners/{profileId}/suspend',
    ADMIN_MARKETPLACE_PARTNER_REMOVE: 'admin/marketplace/partners/{profileId}/remove',
    ADMIN_MARKETPLACE_PARTNER_LINK: 'admin/marketplace/partners/link/{partnerId}',
    ADMIN_MARKETPLACE_PARTNER_LINK_TO_PROFILE: 'admin/marketplace/partners/{profileId}/link/{partnerId}',
    ADMIN_MARKETPLACE_PARTNER_UNLINK: 'admin/marketplace/partners/{profileId}/unlink',
    ADMIN_MARKETPLACE_LEADS: 'admin/marketplace/leads',
    ADMIN_MARKETPLACE_LEAD_STATUS: 'admin/marketplace/leads/{leadId}/status',
    ADMIN_MARKETPLACE_CATEGORIES: 'admin/marketplace/categories',
    ADMIN_MARKETPLACE_CATEGORY: 'admin/marketplace/categories/{categoryId}',
    ADMIN_MARKETPLACE_AUDIT: 'admin/marketplace/audit',
    ADMIN_MARKETPLACE_ATTRIBUTION: 'admin/marketplace/attribution',
    ADMIN_MARKETPLACE_RESEARCH_TEMPLATE: 'admin/marketplace/research/template',
    ADMIN_MARKETPLACE_RESEARCH_COMPANIES: 'admin/marketplace/research/companies',
    ADMIN_MARKETPLACE_RESEARCH_COMPANY: 'admin/marketplace/research/companies/{researchCompanyId}',
    ADMIN_MARKETPLACE_RESEARCH_PREVIEW: 'admin/marketplace/research/preview',
    ADMIN_MARKETPLACE_RESEARCH_IMPORT: 'admin/marketplace/research/import',
    ADMIN_MARKETPLACE_RESEARCH_STATUS: 'admin/marketplace/research/companies/{researchCompanyId}/status',
    ADMIN_MARKETPLACE_RESEARCH_BULK_PUBLISH: 'admin/marketplace/research/companies/bulk-publish',
    ADMIN_MARKETPLACE_RESEARCH_PARTNERS: 'admin/marketplace/research/partners',
    ADMIN_MARKETPLACE_RESEARCH_LINK_PREVIEW: 'admin/marketplace/research/companies/{researchCompanyId}/link-preview',
    ADMIN_MARKETPLACE_RESEARCH_LINK: 'admin/marketplace/research/companies/{researchCompanyId}/link',
    ADMIN_MARKETPLACE_RESEARCH_UNLINK: 'admin/marketplace/research/companies/{researchCompanyId}/unlink',
    ADMIN_MARKETPLACE_RESEARCH_PUBLISH: 'admin/marketplace/research/companies/{researchCompanyId}/marketplace',
    ADMIN_PARTNER_PROGRAMMES: 'admin/partner-programmes',
    ADMIN_PARTNER_PROGRAMME: 'admin/partner-programmes/{programmeId}',
    ADMIN_PARTNER_PROGRAMME_DEFAULTS: 'admin/partner-programmes/defaults',
    ADMIN_PARTNER_PROGRAMME_ACTIVATE: 'admin/partner-programmes/{programmeId}/activate',
    ADMIN_PARTNER_PROGRAMME_DEACTIVATE: 'admin/partner-programmes/{programmeId}/deactivate',
    ADMIN_PARTNER_PROGRAMME_ARCHIVE: 'admin/partner-programmes/{programmeId}/archive',
    ADMIN_PARTNER_PROGRAMME_SET_DEFAULT: 'admin/partner-programmes/{programmeId}/set-default',
    ADMIN_PARTNER_ASSIGN_PROGRAMME: 'admin/partners/{partnerId}/programme',
    ADMIN_TESTING_SIMULATE_INVOICE: 'admin/testing/due-users/{userId}/simulate-invoice',
    ADMIN_TESTING_PROMOTION_PRODUCTS: 'admin/testing/promotions/products',
    ADMIN_TESTING_PROMOTION_SIMULATE: 'admin/testing/promotions/simulate',
    ADMIN_TESTING_PROMOTION_EMAILS: 'admin/testing/promotions/send-test-emails',
    ADMIN_TESTING_FORCE_SIMULATE_INVOICE: 'admin/testing/users/{userId}/force-simulate-invoice',
    ADMIN_TESTING_PAYMENT_EVENT: 'admin/testing/invoices/{invoiceNo}/payment-events/{eventAction}',
    ADMIN_TESTING_INVOICE_COMMISSIONS: 'admin/testing/invoices/{invoiceNo}/commissions',
    ADMIN_TESTING_GENERATE_COMMISSIONS: 'admin/testing/invoices/{invoiceNo}/generate-commissions',
    ADMIN_PARTNER_USERS: 'admin/partners/{partnerId}/users',
    ADMIN_USER_PARTNERS: 'admin/partners/users/{userId}/partners',
    ADMIN_PARTNER_USER: 'admin/partners/{partnerId}/users/{userId}',
    ADMIN_PARTNER_APPLICATIONS: 'admin/partner-applications',
    ADMIN_PARTNER_APPLICATION: 'admin/partner-applications/{applicationId}',
    ADMIN_PARTNER_APPLICATION_UNDER_REVIEW: 'admin/partner-applications/{applicationId}/under-review',
    ADMIN_PARTNER_APPLICATION_APPROVE: 'admin/partner-applications/{applicationId}/approve',
    ADMIN_PARTNER_APPLICATION_REJECT: 'admin/partner-applications/{applicationId}/reject',
    PARTNER_APPLICATIONS: 'partner-applications',
    ADMIN_SYSTEM_STATUS_SUMMARY: 'admin/system-status/summary',
    ADMIN_SYSTEM_STATUS_MAINTENANCE: 'admin/system-status/maintenance',
    ADMIN_SYSTEM_STATUS_MONITORING_CONFIG: 'admin/system-status/monitoring-config',
    ADMIN_SYSTEM_STATUS_CHECK_ACKNOWLEDGEMENT: 'admin/system-status/checks/{checkKey}/acknowledgement',
    ADMIN_SYSTEM_STATUS_POD_RESTART: 'admin/system-status/pods/{podName}/restart',
    ADMIN_SYSTEM_STATUS_POD_EVICT: 'admin/system-status/pods/{podName}/evict',
    ADMIN_SYSTEM_STATUS_POD_LOGS: 'admin/system-status/pods/{podName}/logs',
    ADMIN_STATS_SUMMARY: 'admin/stats/summary',
    ADMIN_STATS_SEND_NOW: 'admin/stats/send-now',
    ADMIN_PROMOTIONS: 'admin/promotions',
    ADMIN_PROMOTIONS_OPTIONS: 'admin/promotions/options',
    ADMIN_PROMOTION: 'admin/promotions/{id}',
    ADMIN_PROMOTION_RECIPIENTS: 'admin/promotions/{id}/recipients',
    ADMIN_PROMOTION_ADD: 'admin/promotions/{id}/recipients/add',
    ADMIN_PROMOTION_IMPORT: 'admin/promotions/{id}/recipients/import',
    ADMIN_PROMOTION_SEND: 'admin/promotions/{id}/send',
    ADMIN_PROMOTION_RECIPIENT_SEND: 'admin/promotions/{id}/recipients/{recipientId}/send',
    ADMIN_PROMOTION_RECIPIENT_DISABLE: 'admin/promotions/{id}/recipients/{recipientId}/disable',
    ADMIN_PROMOTION_RECIPIENT_REPLACE: 'admin/promotions/{id}/recipients/{recipientId}/replace-code',
    ADMIN_PROMOTION_RECIPIENT_DELETE: 'admin/promotions/{id}/recipients/{recipientId}',
    ADMIN_PROMOTION_RECIPIENTS_DELETE_BULK: 'admin/promotions/{id}/recipients/delete',
    ADMIN_PROMOTION_EXPORT: 'admin/promotions/{id}/export',
    PROMOTIONS_VALIDATE: 'promotions/validate',
    PROMOTIONS_REDEEM: 'promotions/redeem',
    ADMIN_AFFILIATE_COMMISSION_APPROVE: 'admin/affiliate-commissions/{commissionId}/approve',
    ADMIN_DEATH_REPORTS: 'admin/death-reports',
    ADMIN_DEATH_REPORT: 'admin/death-reports/{deathReportId}',
    ADMIN_DEATH_REPORT_CERTIFICATE: 'admin/death-reports/{deathReportId}/certificate',
    ADMIN_DEATH_REPORT_DECISION: 'admin/death-reports/{deathReportId}/decision',
    ADMIN_AFFILIATE_COMMISSION_MARK_PAID: 'admin/affiliate-commissions/{commissionId}/mark-paid',
    ADMIN_AFFILIATE_COMMISSION_VOID: 'admin/affiliate-commissions/{commissionId}/cancel',
    ADMIN_AFFILIATE_COMMISSION_STATUS: 'admin/affiliate-commissions/{commissionId}/status',
    ADMIN_AFFILIATE_COMMISSION_BATCH_STATUS: 'admin/affiliate-commissions/batch-status',
    CONTENT_CATALOG_SNAPSHOT: 'admin/content-catalog/snapshot',
    ASSISTANT_CHAT: 'assistant/chat',
    CONTENT_CATALOG_CATEGORIES: 'admin/content-catalog/categories',
    CONTENT_CATALOG_CATEGORY: 'admin/content-catalog/categories/{categoryId}',
    CONTENT_CATALOG_CATEGORY_CARDS: 'admin/content-catalog/categories/{categoryId}/cards',
    CONTENT_CATALOG_CARD_TEMPLATE_OCR_PROFILE: 'admin/content-catalog/card-templates/{cardTemplateId}/ocr-profile',
    CONTENT_CATALOG_CARD_TEMPLATE_OCR_PROFILE_ITEM: 'admin/content-catalog/card-templates/{cardTemplateId}/ocr-profile/{profileId}',
    OCR_PROFILE_PRODUCTION_SYNC_BATCH: 'admin/document-processing/ocr-profile-publication/production-sync',
    OCR_PROFILE_PRODUCTION_DIFF: 'admin/document-processing/ocr-profile-publication/{profileId}/production-diff',
    OCR_PROFILE_PUBLISH: 'admin/document-processing/ocr-profile-publication/{profileId}/publish',
    OCR_PROFILE_PULL_FROM_PRODUCTION: 'admin/document-processing/ocr-profile-publication/{profileId}/pull-from-production',
    OCR_PROFILE_DEPLOYMENT_HISTORY: 'admin/document-processing/ocr-profile-publication/{profileId}/deployment-history',
    CONTENT_CATALOG_CATEGORY_SETS: 'admin/content-catalog/category-sets',
    CONTENT_CATALOG_CATEGORY_SET: 'admin/content-catalog/category-sets/{categorySetId}',
    CONTENT_CATALOG_CATEGORY_SET_DEFAULT_PACKAGE: 'admin/content-catalog/category-sets/{categorySetId}/default-package',
    CONTENT_CATALOG_PACKAGES: 'admin/content-catalog/packages',
    CONTENT_CATALOG_PACKAGE: 'admin/content-catalog/packages/{packageId}',
    CONTENT_CATALOG_PACKAGE_CATEGORY_SETS: 'admin/content-catalog/packages/{packageId}/category-sets',
    MENU_MGMT_SNAPSHOT: 'admin/menu-management/snapshot',
    MENU_MGMT_EXPORT: 'admin/menu-management/export',
    MENU_MGMT_IMPORT: 'admin/menu-management/import',
    MENU_MGMT_MENUS: 'admin/menu-management/menus',
    MENU_MGMT_MENU: 'admin/menu-management/menus/{menuId}',
    MENU_MGMT_SUBMENUS: 'admin/menu-management/submenus',
    MENU_MGMT_SUBMENU: 'admin/menu-management/submenus/{subMenuId}',
    MENU_MGMT_ENDPOINTS: 'admin/menu-management/endpoints',
    MENU_MGMT_ENDPOINT: 'admin/menu-management/endpoints/{endpointId}',
    MENU_MGMT_FOOTER_ITEMS: 'admin/menu-management/footer-items',
    MENU_MGMT_FOOTER_ITEM: 'admin/menu-management/footer-items/{footerMenuId}',
    MENU_MGMT_PRODUCTION_SYNC: 'admin/menu-management/publication/{itemType}/{itemId}/production-sync',
    MENU_MGMT_PRODUCTION_SYNC_BATCH: 'admin/menu-management/publication/production-sync',
    MENU_MGMT_PRODUCTION_DIFF: 'admin/menu-management/publication/{itemType}/{itemId}/production-diff',
    MENU_MGMT_PUBLISH: 'admin/menu-management/publication/{itemType}/{itemId}/publish',
    MENU_MGMT_PULL_FROM_PRODUCTION: 'admin/menu-management/publication/{itemType}/{itemId}/pull-from-production',
    MENU_MGMT_DEPLOYMENT_HISTORY: 'admin/menu-management/publication/{itemType}/{itemId}/deployment-history',
    ADMIN_TAKE_ON_QUESTIONS_SNAPSHOT: 'admin/take-on-questions/snapshot',
    ADMIN_TAKE_ON_QUESTIONS: 'admin/take-on-questions/questions',
    ADMIN_TAKE_ON_QUESTION: 'admin/take-on-questions/questions/{questionId}',
    ADMIN_TAKE_ON_QUESTION_OPTIONS: 'admin/take-on-questions/options',
    ADMIN_TAKE_ON_QUESTION_OPTION: 'admin/take-on-questions/options/{optionId}',
    ADMIN_TAKE_ON_QUESTION_OPTION_LIFESTAGE_DEFAULTS: 'admin/take-on-questions/options/{optionId}/life-stage-priority-defaults',
    // NOTIFICATIONS (SendMail -> notifications/*)
    SEND_EMAIL: 'notifications/email',                    // OLD: SendMail

    // LOGGING (eventlog/* -> logs/*)
    GET_EVENT_LOGS: 'logs',
    GET_EVENT_LOG_SUMMARY: 'logs/summary',
    ARCHIVE_EVENT_LOGS: 'logs/archive',
    ACKNOWLEDGE_EVENT_LOGS: 'logs/acknowledge',
    LOG_ERROR: 'logs/errors',                             // OLD: eventlog/error-logs

    // EMAIL MANAGEMENT
    EMAIL_MGMT_SUMMARY: 'admin/email-management/summary',
    EMAIL_MGMT_WORKERS: 'admin/email-management/workers',
    EMAIL_MGMT_WORKER: 'admin/email-management/workers/{workerId}',
    EMAIL_MGMT_WORKER_PAUSE: 'admin/email-management/workers/{workerId}/pause',
    EMAIL_MGMT_QUEUE: 'admin/email-management/queue',
    EMAIL_MGMT_QUEUE_ITEM: 'admin/email-management/queue/{queueId}',
    EMAIL_MGMT_QUEUE_LOGS: 'admin/email-management/queue/{queueId}/logs',
    EMAIL_MGMT_QUEUE_STATUS: 'admin/email-management/queue/{queueId}/status',
    EMAIL_MGMT_READINESS_NOTIFICATIONS: 'admin/email-management/readiness-notifications',
    EMAIL_MGMT_TEMPLATES: 'admin/email-management/templates',
    EMAIL_MGMT_TEMPLATES_DOWNLOAD_ALL: 'admin/email-management/templates/download-all',
    EMAIL_MGMT_TEMPLATE: 'admin/email-management/templates/{templateName}',
    EMAIL_MGMT_TEMPLATE_SEND_TEST: 'admin/email-management/templates/{templateName}/send-test',
    EMAIL_MGMT_TEMPLATES_UPLOAD: 'admin/email-management/templates/upload',
    EMAIL_MGMT_TEMPLATE_REFRESH: 'admin/email-management/template-refresh',
    ADMIN_NURTURE: 'admin/nurture',
    ADMIN_NURTURE_USERS: 'admin/nurture/users',
    ADMIN_NURTURE_QUEUE_DUE: 'admin/nurture/queue-due',
    ADMIN_NURTURE_USER_SEND: 'admin/nurture/users/{userId}/send',
    ADMIN_NURTURE_SEQUENCE: 'admin/nurture/sequences/{sequenceId}',
    ADMIN_NURTURE_STEP: 'admin/nurture/steps/{stepId}',
    
    //FieldChoice
    GET_FIELD_CHOICE: 'fieldchoice/list',
    SAVE_FIELD_CHOICE: 'fieldchoice',

    //GOCARDLESS
    CREATE_CUSTORMER: 'gocardless/CreateCustomer',
    CREATE_BANK_ACCOUNT: 'gocardless/CreateBankAccount',
    CREATE_MANDATE: 'gocardless/mandate',
    INITIATE_BILLING: 'gocardless/initiatebilling',
    DIRECT_DEBIT_SETUP: 'gocardless/direct-debit/setup',
    DIRECT_DEBIT_RETURN: 'gocardless/direct-debit/return',
    CHANGE_MANDATE: 'gocardless/mandate-change',
    CREATE_SUBSCRIPTION: 'gocardless/subscriptions',
    GET_BILLING_STATUS:'gocardless/billing-status/{billingRequestId}',

    BRANDING_ALL: 'brands',
    BRANDING_FAMILY: 'brands/family',
    BRANDING_BY_ID: 'brands/{brandId}',
    BRANDING_PRIMARY: 'brands/primary',
    PORTAL_CONTEXT: 'partners/portal-context',
    GET_PROFILE: 'profile',
    GET_PROFILE_PALETTES: 'profile/palettes',
    GET_PROFILE_BRANDS: 'profile/brands',
    PROFILE_READINESS_NOTIFICATIONS: 'profile/readiness-notifications',
    PROFILE_MARKETING_EMAILS: 'profile/marketing-emails',
    PROFILE_PHOTO: 'profile/photo',
    USER_PROFILE_PHOTO: 'profile/users/{targetUserId}/photo',
    TAKE_ON_CURRENT: 'take-on/current',
    TAKE_ON_QUESTION: 'take-on/questions/{questionCode}',
    TAKE_ON_COMPLETE: 'take-on/complete',
    TAKE_ON_RESTART: 'take-on/restart',
    FAMILY_VAULT_BRAND: 'family-vault/brand',
    GET_ACTIVITY: 'activity',
    EXPORT_SNAPSHOT: 'exports/snapshot',
    EMERGENCY_ACCESS_SETTINGS: 'emergency-access/settings',

    // EXECUTOR MANAGEMENT (probate/executors/*)
    PROBATE_EXECUTORS: 'probate/executors',
    PROBATE_EXECUTOR_RESEND: 'probate/executors/{id}/resend',
    PROBATE_EXECUTOR_RULES: 'probate/executors/rules',
    PROBATE_EXECUTOR_GENERATE_KEYS: 'probate/executors/generate-keys',
    PROBATE_EXECUTOR_PUBLIC_KEYS: 'probate/executors/public-keys',
    PROBATE_EXECUTOR_PREPARE: 'probate/executors/prepare',

    // EXECUTOR PORTAL (executor/*)
    EXECUTOR_NOMINATIONS: 'executor/nominations',
    EXECUTOR_NOMINATION_RESPOND: 'executor/nominations/{executorId}/respond',
    EXECUTOR_NOMINATION_INVITE_KEY: 'executor/nominations/{executorId}/invite-key',
    EXECUTOR_NOMINATION_REWRAP: 'executor/nominations/{executorId}/rewrap-share',
    EXECUTOR_CASES: 'executor/cases',
    EXECUTOR_CASE_DETAIL: 'executor/cases/{caseId}',
    EXECUTOR_MY_SHARE: 'executor/cases/{caseId}/my-share',
    EXECUTOR_KC: 'executor/cases/{caseId}/kc',
    EXECUTOR_PROBATE_KEY: 'executor/cases/{caseId}/probate-key',
    EXECUTOR_DEATH_REPORT: 'executor/cases/{caseId}/death-report',
    EXECUTOR_UPLOADS: 'uploads',
} as const;

/**
 * Type for route keys
 */
export type RouteKey = keyof typeof Routes;

/**
 * Type for route values
 */
export type RouteValue = typeof Routes[RouteKey];
